import { ExpandLess, ExpandMore, Mail as MailIcon } from '@mui/icons-material';
import { InputLabel, Select, TextField } from '@mui/material';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { postCampaignAnalyticsReportAction } from 'store/actions/advocacyReports';
import { setSnackbarError, setSnackbarSuccess } from 'store/actions/global';
import {
  BuildButton,
  DatesWrapper,
  OptionsSection,
  OptionsToggle,
  ReportDescription,
  ReportTitle,
  StyledCard,
  StyledFormControl,
} from '../styles';

const CampaignAnalyticsReport = ({ campaignUuid, organizationUuid, reportsSchema }) => {
  const dispatch = useDispatch();

  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const handleOptionsToggle = () => setShowOptions(prevState => !prevState);

  const handleBuildReport = () => {
    if (campaignUuid) {
      const payload = {
        campaign: campaignUuid,
      };

      if (startDate) {
        payload.start_date = dayjs(startDate).format('YYYY-MM-DD');
      }

      if (endDate) {
        payload.end_date = dayjs(endDate).format('YYYY-MM-DD');
      }

      dispatch(postCampaignAnalyticsReportAction(payload, organizationUuid))
        .then(result => {
          dispatch(setSnackbarSuccess({ message: result?.detail }));
        })
        .catch(() => {
          dispatch(
            setSnackbarError({
              message: 'Failed to build report. Make sure all fields are set properly.',
            })
          );
        });

      return;
    }

    if (!selectedCampaign) {
      dispatch(setSnackbarError({ message: 'Please select a campaign' }));

      return;
    }

    const foundCampaign = reportsSchema?.campaigns?.find(c => c.uuid === selectedCampaign);

    if (!foundCampaign) return;

    const payload = {
      campaign: foundCampaign.uuid,
    };

    if (startDate) {
      payload.start_date = dayjs(startDate).format('YYYY-MM-DD');
    }

    if (endDate) {
      payload.end_date = dayjs(endDate).format('YYYY-MM-DD');
    }

    dispatch(postCampaignAnalyticsReportAction(payload, organizationUuid))
      .then(result => {
        dispatch(setSnackbarSuccess({ message: result?.detail }));
      })
      .catch(() => {
        dispatch(
          setSnackbarError({
            message: 'Failed to build report. Make sure all fields are set properly.',
          })
        );
      });
  };

  return (
    <StyledCard variant="outlined">
      <ReportTitle variant="h6">Campaign Analytics Report</ReportTitle>
      <ReportDescription variant="body2">
        {campaignUuid
          ? 'Export analytics for this campaign.'
          : 'Export analytics for a specific campaign.'}
      </ReportDescription>

      {!campaignUuid && (
        <StyledFormControl variant="outlined">
          <InputLabel htmlFor="campaign-native-select-analytics" sx={{ backgroundColor: 'white' }}>
            Campaign
          </InputLabel>
          <Select
            variant="outlined"
            native
            value={selectedCampaign}
            onChange={e => setSelectedCampaign(e.target.value)}
            label="Campaign"
            inputProps={{
              id: 'campaign-native-select-analytics',
              name: 'campaign',
            }}
          >
            <option aria-label="None" value="" />
            {reportsSchema?.campaigns?.map(campaign => (
              <option key={campaign.uuid} value={campaign.uuid}>
                {campaign.name}
              </option>
            ))}
          </Select>
        </StyledFormControl>
      )}

      <OptionsToggle onClick={handleOptionsToggle}>
        {showOptions ? 'Hide options' : 'Options'}
        {showOptions ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </OptionsToggle>

      {showOptions && (
        <OptionsSection>
          <DatesWrapper>
            <TextField
              label="Start date"
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
            <TextField
              label="End date"
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </DatesWrapper>
        </OptionsSection>
      )}

      <BuildButton
        variant="contained"
        color="primary"
        startIcon={<MailIcon />}
        onClick={handleBuildReport}
      >
        Build Report
      </BuildButton>
    </StyledCard>
  );
};

export default CampaignAnalyticsReport;

CampaignAnalyticsReport.defaultProps = {
  campaignUuid: null,
  reportsSchema: null,
};

CampaignAnalyticsReport.propTypes = {
  campaignUuid: PropTypes.string,
  organizationUuid: PropTypes.string.isRequired,
  reportsSchema: PropTypes.shape({
    campaigns: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        uuid: PropTypes.string,
      })
    ),
  }),
};
